import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import * as _ from 'lodash';
import { NoUnderscorePipe } from 'app/shared/pipes/no-underscore/no-underscore.pipe';
import { TranslateService } from '@ngx-translate/core';

export enum DataExportType {
    xlsx = 1,
    csv = 2
    // can easily be extended to json, html etc.
}

@Injectable({
    providedIn: 'root'
})
export class ExcelExportService {
    static readonly exportedFileOptimisationOptions = {compression: true, cellStyles: false};

    constructor(
        private noUnderscorePipe: NoUnderscorePipe,
        private translate: TranslateService
        ) { }

    private static saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([ buffer ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        });
        FileSaver.saveAs(data, fileName + '.xlsx');
    }

    private static saveAsCsvFile(buffer: any, fileName: string): void {
        const BOM = '\uFEFF';
        const csvData = BOM + buffer;
        const data: Blob = new Blob([ csvData ], { type: 'text/csv;charset=utf-8' });
        FileSaver.saveAs(data, fileName + '.csv');
    }

    public exportLogbookTableAsFile(
        tableElement: any,
        fileName: string,
        exportType: DataExportType,
        view: string
    ): void {
        const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(tableElement);
        if (view === 'general' || view === 'losses_review') {
            Object.keys(ws).forEach((key, index) => {
                if ((view === 'losses_review' && (key.includes('B') && key !== 'B1' && ws[key].t === 's')) || (view === 'losses_review' && (key.includes('D') && key !== 'D1' && ws[key].t === 's'))) {
                  ws[key].v = ws[key].v.split(' ')[0];
                }
                if ((view === 'general' && key.includes('C') && ws[key].t === 'n') || (view === 'losses_review' && (key.includes('B') && ws[key].t === 'n') || (key.includes('D') && ws[key].t === 'n') )) {
                    ws[key].z = 'mm/dd/yyyy';
                    XLSX.utils.format_cell(ws[key]);
                    ws[key].t = 's';
                    ws[key].v = ws[key].w.replaceAll('/', '.');
                    ws[key].v = ws[key].v.split(' ')[0];
                } else {
                  if (view === 'general' && key.includes('C') && ws[key].t === 's') {
                    ws[key].v = ws[key].v.split(' ')[0];
                  }
                }
            });
        }
        let excelInJSON;
        if (view !== 'losses_review') {
          excelInJSON = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }).filter((e) => e[1] !== '');
          const colsToDelete = view === 'general' ? [ 9, 8, 7, 6, 0 ] : [ 8, 7, 6, 5, 0 ];
          excelInJSON.forEach((e) => {
            colsToDelete.forEach((r) => {
              e.splice(r, 1);
            });
          });
        } else {
          excelInJSON = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
          const colsToDelete = [ 14, 13 ];
          excelInJSON.forEach((e) => {
              colsToDelete.forEach((r) => {
                  e.splice(r, 1);
              });
          });
        }
        const opts: XLSX.AOA2SheetOpts = null;
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        const rawData = XLSX.utils.aoa_to_sheet(excelInJSON, opts);
        rawData['!cols'] =
            view === 'general'
                ? [
                      { wpx: 35 },
                      { wpx: 80 },
                      { wpx: 80 },
                      { wpx: 80 },
                      { wpx: 80 },
                      { wpx: 270 },
                      { wpx: 160 },
                      { wpx: 100 },
                      { wpx: 100 }
                  ]
                : view === 'daily' ? [
                      { wpx: 35 },
                      { wpx: 80 },
                      { wpx: 80 },
                      { wpx: 80 },
                      { wpx: 270 },
                      { wpx: 160 },
                      { wpx: 100 },
                      { wpx: 100 }
                  ] :
                      [{ wpx: 40 },
                      { wpx: 80 },
                      { wpx: 80 },
                      { wpx: 80 },
                      { wpx: 80 },
                      { wpx: 80 },
                      { wpx: 270 },
                      { wpx: 170 },
                      { wpx: 160 },
                      { wpx: 180 },
                      { wpx: 200 },
                      { wpx: 200 },
                      { wpx: 120 }]
                  ;
        XLSX.utils.book_append_sheet(wb, rawData, 'Logbook Table');

        if (exportType === 1) {
            const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            ExcelExportService.saveAsExcelFile(excelBuffer, fileName);
        } else {
            const csvData = XLSX.utils.sheet_to_csv(rawData);
            ExcelExportService.saveAsCsvFile(csvData, fileName);
        }
    }

    public exportTableAsFile(tableElement: any, fileName: string, exportType: DataExportType): void {
        const worksheet: XLSX.WorkSheet = XLSX.utils.table_to_sheet(tableElement, { raw: true });

        // in order to make number detection and calculations possible in Excel & Numbers app on Mac
        // we need to re-format the numbers and remove the apostrophe from numbers (123'456,78 => 123456,78)
        let rowNum;
        let colNum;
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        for (rowNum = range.s.r; rowNum <= range.e.r; rowNum++) {
            for (colNum = range.s.c; colNum <= range.e.c; colNum++) {
                const cell = worksheet[XLSX.utils.encode_cell({ r: rowNum, c: colNum })];
                let cellWithoutApostrophe;
                if (cell) {
                  cellWithoutApostrophe = cell.v.replaceAll(`'`, '');
                }
                if (!isNaN(cellWithoutApostrophe)) {
                    cell.v = cellWithoutApostrophe;
                    cell.t = 'n'; // set excel cell type to 'number'
                }
            }
        }

        switch (exportType) {
            case DataExportType.xlsx: {
                const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: [ 'data' ] };
                const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                ExcelExportService.saveAsExcelFile(excelBuffer, fileName);
                break;
            }
            case DataExportType.csv: {
                const csvData = XLSX.utils.sheet_to_csv(worksheet);
                const lines = csvData.split('\n');
                const linesNoVacias = lines.filter(line => line.trim() !== '' && !/^,+$/.test(line));
                const csvDataFiltered = linesNoVacias.join('\n');
                ExcelExportService.saveAsCsvFile(csvDataFiltered, fileName);
                break;
            }
            default:
                return;
        }
    }

    public exportJSONasTable(JSONdata: any, fileName: string, exportType: DataExportType): void {
        let sheets = [];
        const seriesData = XLSX.utils.json_to_sheet(JSONdata);
        const workbook: XLSX.WorkBook = { Sheets: { data: [] }, SheetNames: [] };
        sheets.forEach(s => {
          XLSX.utils.book_append_sheet(workbook, s.sheetData, s.title);
        });
        XLSX.utils.book_append_sheet(workbook, seriesData, 'Series Data');
  
        switch (exportType) {
            case DataExportType.xlsx: {
                const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', ...ExcelExportService.exportedFileOptimisationOptions });
                ExcelExportService.saveAsExcelFile(excelBuffer, fileName);
                break;
            }
            case DataExportType.csv: {
                const plantDataForCSV = XLSX.utils.json_to_sheet(JSONdata);
                ExcelExportService.saveAsCsvFile(XLSX.utils.sheet_to_csv(plantDataForCSV), fileName);
                break;
            }
            default:
                return;
        }
      }
  
    public exportGridCurtailmentsTableAsFile(tableElement: any, fileName: string, exportType: DataExportType): void {
      const worksheet: XLSX.WorkSheet = XLSX.utils.table_to_sheet(tableElement, { raw: true });
      let rowNum;
      let colNum;
      worksheet['!merges'] = [];
      Object.keys(worksheet).forEach((key, index) => {
        if (key.includes('A')) {
          worksheet[key].v = worksheet[key].v.length > 50 ? '' : worksheet[key].v;
        }
      })
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      for (rowNum = range.s.r; rowNum <= range.e.r; rowNum++) {
          for (colNum = range.s.c; colNum <= range.e.c; colNum++) {
              const cell = worksheet[XLSX.utils.encode_cell({ r: rowNum, c: colNum })];
              let cellWithoutApostrophe;
              if (cell) {
                cellWithoutApostrophe = cell.v.replaceAll(`'`, '');
              }
              if (!isNaN(cellWithoutApostrophe)) {
                  cell.v = cellWithoutApostrophe;
                  cell.t = 'n';
              }
          }
      }

      switch (exportType) {
          case DataExportType.xlsx: {
              const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: [ 'data' ] };
              const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
              ExcelExportService.saveAsExcelFile(excelBuffer, fileName);
              break;
          }
          case DataExportType.csv: {
              const csvData = XLSX.utils.sheet_to_csv(worksheet);
              ExcelExportService.saveAsCsvFile(csvData, fileName);
              break;
          }
          default:
              return;
      }
    }

    public exportLogbookParkSummaryTableAsFile(JSONdata: any, fileName: string, exportType: DataExportType, isDataStudio): void {
      let sheets = [];
      const dataByTurbine = _.groupBy(JSONdata, 'Turbine');
      const parkData = XLSX.utils.json_to_sheet(JSONdata);
      Object.keys(dataByTurbine).forEach(k => {
        sheets.push({title: k, data: dataByTurbine[k], sheetData: XLSX.utils.json_to_sheet(dataByTurbine[k])})
      });
      sheets = sheets.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' }));
    
      const workbook: XLSX.WorkBook = { Sheets: { data: [] }, SheetNames: [] };
      sheets.forEach(s => {
        XLSX.utils.book_append_sheet(workbook, s.sheetData, s.title);
      });
      XLSX.utils.book_append_sheet(workbook, parkData, 'Park');
    
      switch (exportType) {
          case DataExportType.xlsx: {
              const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
              ExcelExportService.saveAsExcelFile(excelBuffer, fileName);
              break;
          }
          case DataExportType.csv: {
              const JSONDataSorted = isDataStudio ? JSONdata : JSONdata.sort((a, b) => a.Turbine.localeCompare(b.Turbine, undefined, { numeric: true, sensitivity: 'base' }));
              const parkDataForCSV = XLSX.utils.json_to_sheet(JSONDataSorted);
              ExcelExportService.saveAsCsvFile(XLSX.utils.sheet_to_csv(parkDataForCSV), fileName);
              break;
          }
          default:
              return;
      }
    }


    public exportDataStudioMetadataAsFile(JSONdata: any, fileName: string, showPark= true): void {
        if (JSONdata.hasOwnProperty('energyMeterAvailable')) {
            JSONdata.energyMeterAvailable = JSONdata.energyMeterAvailable ? this.translate.instant('COMMON.YES') : this.translate.instant('COMMON.NO');
          }
          let turbines = JSONdata.turbines.sort((a, b) => a.code?.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' }));

        delete JSONdata.turbines;
        const showTurbines = !turbines.every(obj => Object.keys(obj).length === 0);
        const workbook: XLSX.WorkBook = { Sheets: { data: [] }, SheetNames: [] };

        if(showPark) {
            const parkSheet  = XLSX.utils.json_to_sheet([JSONdata]);
            XLSX.utils.book_append_sheet(workbook, parkSheet , 'Park');
        }

        if(showTurbines) {
            for (let obj of turbines) {
                if (obj.hasOwnProperty('code')) {
                    obj['code'] = this.noUnderscorePipe.transform(obj['code']);            
                }
            }
            const turbineSheet = XLSX.utils.json_to_sheet(turbines, { header: ['code', ...Object.keys(turbines[0]).filter(key => key !== 'code')]});
            XLSX.utils.book_append_sheet(workbook, turbineSheet, 'Turbines');
        }

        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        ExcelExportService.saveAsExcelFile(excelBuffer, fileName);
    }

    public exportAsExcelFile(json: any[], excelFileName: string, format: number, columnWidth: {wpx: number}[] = []): void {
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
        if (columnWidth.length > 0) worksheet['!cols'] = columnWidth;
        if (format === 0) {
                const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: [ 'data' ] };
                const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                ExcelExportService.saveAsExcelFile(excelBuffer, excelFileName);
        }else {
                const csvData = XLSX.utils.sheet_to_csv(worksheet);
                ExcelExportService.saveAsCsvFile(csvData, excelFileName);
        }
    }

    public exportAsExcelFileWithMultipleSheets(rawdata: any[], fileName: string, format: DataExportType, splitKey:string): void {
        const data = _.cloneDeep(rawdata);
        
        switch (format) {
            case DataExportType.xlsx: {
                let sheets = [];
                const grouped = _.groupBy(data, splitKey);
                const allData = XLSX.utils.json_to_sheet(data);
                Object.keys(grouped).forEach(k => {
                  grouped[k].forEach(d => delete d[splitKey]);
                  sheets.push({title: k, data: grouped[k], sheetData: XLSX.utils.json_to_sheet(grouped[k])})
                });
                sheets = sheets.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' }));
              
                const workbook: XLSX.WorkBook = { Sheets: { data: [] }, SheetNames: [] };
                XLSX.utils.book_append_sheet(workbook, allData, 'All');
                sheets.forEach(s => XLSX.utils.book_append_sheet(workbook, s.sheetData, s.title) );
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                ExcelExportService.saveAsExcelFile(excelBuffer, fileName);
                break;
            }
            case DataExportType.csv: {
                const JSONDataSorted = data.sort((a, b) => a[splitKey].localeCompare(b[splitKey], undefined, { numeric: true, sensitivity: 'base' }));
                const parkDataForCSV = XLSX.utils.json_to_sheet(JSONDataSorted);
                ExcelExportService.saveAsCsvFile(XLSX.utils.sheet_to_csv(parkDataForCSV), fileName);
                break;
            }
        }
    }
}
